import { NavLink } from 'react-router-dom';
import {
  Banknote,
  BookOpenCheck,
  Boxes,
  Building2,
  CheckCircle2,
  ChevronLeft,
  Contact,
  Database,
  FileBarChart,
  FileSignature,
  FileSpreadsheet,
  FileText,
  FolderArchive,
  Handshake,
  HardHat,
  History,
  Landmark,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  type LucideIcon,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Trash2,
  TrendingUp,
  Truck,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { canViewNav } from '@/lib/permissions';
import { useUIStore } from '@/store/uiStore';
import type { CurrentUser } from '@/types';
import { getNonLus } from '@/services/messagerie';
import { NAV_GROUPS } from './navigation';
import { ProfilModal } from './ProfilModal';

/** Résolution nom -> composant Lucide (les noms viennent de navigation.ts). */
const ICONS: Record<string, LucideIcon> = {
  LayoutDashboard,
  ListChecks,
  Mail,
  Contact,
  FileSignature,
  HardHat,
  FileBarChart,
  FileSpreadsheet,
  FileText,
  FolderArchive,
  History,
  Receipt,
  ScrollText,
  TrendingUp,
  BookOpenCheck,
  Building2,
  Landmark,
  Banknote,
  CheckCircle2,
  Truck,
  Users,
  ShoppingCart,
  Handshake,
  Boxes,
  Database,
  Settings,
  ShieldCheck,
  Trash2,
};

export interface SidebarNavProps {
  user?: CurrentUser;
  onLogout?: () => void;
}

/**
 * Sidebar : logo, groupes de navigation avec icônes Lucide, badges de
 * notification, section utilisateur en bas. Brief — composant #6.
 */
export function SidebarNav({ user, onLogout }: SidebarNavProps) {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggle = useUIStore((s) => s.toggleSidebar);
  const mobileOpen = useUIStore((s) => s.mobileOpen);
  const setMobileOpen = useUIStore((s) => s.setMobileOpen);
  const [profilOpen, setProfilOpen] = useState(false);
  // Badge de messages non lus (rafraîchi périodiquement, dès l'ouverture de l'app).
  const { data: msgNonLus } = useQuery({
    queryKey: ['messagerie-nonlus'], queryFn: getNonLus, refetchInterval: 20000, enabled: !!user,
  });
  const msgBadge = msgNonLus?.non_lus ?? 0;

  const initials = user ? `${user.prenom.charAt(0)}${user.nom.charAt(0)}`.toUpperCase() : 'AW';

  return (
    <>
      {/* Backdrop mobile : ferme le tiroir au tap (caché en desktop). */}
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden />}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-screen w-[var(--sidebar-width)] flex-col bg-sidebar-bg text-sidebar-text transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 print:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed && 'lg:w-[var(--sidebar-width-collapsed)]',
        )}
      >
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center gap-2.5 px-4">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-accent text-sm font-extrabold text-brand-accent-contrast">
          AW
        </div>
        <div className={cn('min-w-0', collapsed && 'lg:hidden')}>
          <p className="truncate text-sm font-bold leading-tight text-sidebar-text-active">Advanced Works</p>
          <p className="truncate text-[10px] font-semibold uppercase tracking-widest text-sidebar-text">
            ERP · BTPH
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="scrollbar-thin flex-1 overflow-y-auto px-2.5 py-2">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((it) => (!it.roles || (user && it.roles.includes(user.role))) && canViewNav(user, it.to));
          if (items.length === 0) return null;
          return (
            <div key={group.title} className="mb-4">
              <p className={cn('px-2 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-widest text-sidebar-text/60', collapsed && 'lg:hidden')}>
                {group.title}
              </p>
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const Icon = ICONS[item.icon] ?? LayoutDashboard;
                  // Badge dynamique de messages non lus sur l'entrée Messagerie.
                  const badge = item.to === '/messagerie' && msgBadge > 0 ? (msgBadge > 99 ? '99+' : msgBadge) : item.badge;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/'}
                        title={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          cn(
                            'group flex items-center gap-3 rounded-md border-l-2 border-transparent px-2.5 py-2 text-sm font-medium transition-colors',
                            collapsed && 'lg:justify-center',
                            isActive
                              ? 'border-brand-accent bg-sidebar-active text-sidebar-text-active'
                              : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
                          )
                        }
                        onClick={() => setMobileOpen(false)}
                      >
                        <Icon className="size-[18px] shrink-0" />
                        <span className={cn('flex-1 truncate', collapsed && 'lg:hidden')}>{item.label}</span>
                        {badge ? (
                          <span className={cn('flex h-5 min-w-5 items-center justify-center rounded-full bg-status-stuck px-1.5 text-[10px] font-bold text-white', collapsed && 'lg:hidden')}>
                            {badge}
                          </span>
                        ) : null}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Section utilisateur */}
      <div className="shrink-0 border-t border-white/10 p-2.5">
        <button
          type="button"
          onClick={() => setProfilOpen(true)}
          title="Mon compte"
          className={cn('flex w-full items-center gap-2.5 rounded-md p-1.5 text-left transition-colors hover:bg-sidebar-hover', collapsed && 'lg:justify-center')}
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-secondary text-xs font-bold text-white">
            {initials}
          </div>
          {user && (
            <div className={cn('min-w-0 flex-1', collapsed && 'lg:hidden')}>
              <p className="truncate text-sm font-semibold text-sidebar-text-active">
                {user.prenom} {user.nom}
              </p>
              <p className="truncate text-[11px] capitalize text-sidebar-text">{user.role.toLowerCase()} · Mon compte</p>
            </div>
          )}
        </button>
        <div className={cn('mt-1 flex gap-1', collapsed && 'flex-col')}>
          <button
            onClick={toggle}
            className="flex flex-1 items-center justify-center gap-2 rounded-md py-1.5 text-xs font-medium text-sidebar-text transition-colors hover:bg-sidebar-hover hover:text-sidebar-text-active"
            aria-label={collapsed ? 'Déplier la sidebar' : 'Replier la sidebar'}
          >
            <ChevronLeft className={cn('size-4 transition-transform', collapsed && 'rotate-180')} />
            <span className={cn(collapsed && 'lg:hidden')}>Replier</span>
          </button>
          {onLogout && (
            <button
              onClick={onLogout}
              className="flex items-center justify-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-sidebar-text transition-colors hover:bg-status-stuck hover:text-white"
              aria-label="Se déconnecter"
              title="Se déconnecter"
            >
              <LogOut className="size-4" />
            </button>
          )}
        </div>
      </div>

      <ProfilModal open={profilOpen} onOpenChange={setProfilOpen} />
      </aside>
    </>
  );
}
